import { redirect } from '@tanstack/react-router';
import type {
    RouteProtection,
    AuthContextType,
    RouteProtectionResult,
    ExtendedRouteContext,
    RBACUser,
    RoleName,
    Permission,
} from '../types';
import { canAccessRoute, sanitizeUserPermissions, sanitizeUserRoles, getPermissionsForRoles } from '../utils/rbac-utils';
import { ROUTE_PATHS, ACCESS_ERROR_MESSAGES } from '../constants/rbac';

/**
 * Check if a user can access a route based on protection configuration
 */
export function checkRouteAccess(
    auth: AuthContextType,
    protection: RouteProtection
): RouteProtectionResult {
    // If no protection is configured, allow access
    if (!protection.requireAuth && !protection.requiredRoles && !protection.requiredPermissions) {
        return { canAccess: true, shouldRedirect: false };
    }

    // If still loading, don't make access decisions yet
    if (auth.isLoading) {
        return { canAccess: false, shouldRedirect: false };
    }

    // Check if user can access the route
    const canAccess = canAccessRoute(auth.user, protection);

    if (!canAccess) {
        // Determine redirect path and error message
        let redirectPath = protection.fallbackPath || protection.redirectTo;
        let errorType: 'AUTHENTICATION_REQUIRED' | 'INSUFFICIENT_PERMISSIONS';
        let errorMessage: string;

        if (!auth.isAuthenticated) {
            errorType = 'AUTHENTICATION_REQUIRED';
            errorMessage = ACCESS_ERROR_MESSAGES.AUTHENTICATION_REQUIRED;
            redirectPath = redirectPath || ROUTE_PATHS.LOGIN;
        } else {
            errorType = 'INSUFFICIENT_PERMISSIONS';
            if (protection.requiredRoles && protection.requiredRoles.length > 0) {
                errorMessage = ACCESS_ERROR_MESSAGES.ROLE_REQUIRED(protection.requiredRoles);
            } else if (protection.requiredPermissions && protection.requiredPermissions.length > 0) {
                errorMessage = ACCESS_ERROR_MESSAGES.PERMISSION_REQUIRED(protection.requiredPermissions);
            } else {
                errorMessage = ACCESS_ERROR_MESSAGES.INSUFFICIENT_PERMISSIONS;
            }
            redirectPath = redirectPath || ROUTE_PATHS.ACCESS_DENIED;
        }

        return {
            canAccess: false,
            shouldRedirect: true,
            redirectPath,
            error: {
                type: errorType,
                message: errorMessage,
            },
        };
    }

    return { canAccess: true, shouldRedirect: false };
}

/**
 * Create a beforeLoad function for route protection with caching optimization
 */
export function createRouteGuard(protection: RouteProtection) {
    return async ({ context }: { context: ExtendedRouteContext }) => {
        const { auth } = context; // Better Auth client instance (see types/auth.ts)

        // Try to get cached session first for better performance
        let sessionRes;
        try {
            // Check if we have a recent cached session (within 30 seconds)
            const cachedSession = sessionStorage.getItem('CACHED_SESSION');
            const cacheTimestamp = sessionStorage.getItem('CACHED_SESSION_TIMESTAMP');
            
            if (cachedSession && cacheTimestamp) {
                const age = Date.now() - parseInt(cacheTimestamp);
                if (age < 30000) { // 30 seconds cache
                    sessionRes = JSON.parse(cachedSession);
                } else {
                    // Cache expired, remove it
                    sessionStorage.removeItem('CACHED_SESSION');
                    sessionStorage.removeItem('CACHED_SESSION_TIMESTAMP');
                }
            }
            
            // If no cached session or cache expired, fetch fresh session
            if (!sessionRes) {
                sessionRes = await auth.getSession();
                
                // Cache the session for subsequent route navigations
                if (sessionRes) {
                    sessionStorage.setItem('CACHED_SESSION', JSON.stringify(sessionRes));
                    sessionStorage.setItem('CACHED_SESSION_TIMESTAMP', Date.now().toString());
                }
            }
        } catch (error) {
            // Fallback to regular session fetch on any cache error
            sessionRes = await auth.getSession();
        }

        // Helper: build RBACUser from Better Auth session response
        const buildRBACUser = (): RBACUser | null => {
            const betterUser = sessionRes?.user as any;
            const betterSession = sessionRes?.session as any;
            if (!betterUser || !betterSession) return null;

            // Prefer roles/permissions from session augmentation
            let roles: RoleName[] = Array.isArray(betterSession?.roles)
                ? sanitizeUserRoles(betterSession.roles)
                : [];

            let permissions: Permission[] = Array.isArray(betterSession?.permissions)
                ? sanitizeUserPermissions(betterSession.permissions)
                : [];

            // Fallback to user fields if session arrays are absent
            if (roles.length === 0 && betterUser?.roles) {
                if (typeof betterUser.roles === 'string') {
                    try { roles = sanitizeUserRoles(JSON.parse(betterUser.roles)); } catch { roles = sanitizeUserRoles([betterUser.roles]); }
                } else if (Array.isArray(betterUser.roles)) {
                    roles = sanitizeUserRoles(betterUser.roles);
                }
            }

            if (permissions.length === 0 && betterUser?.permissions) {
                if (typeof betterUser.permissions === 'string') {
                    try { permissions = sanitizeUserPermissions(JSON.parse(betterUser.permissions)); } catch { permissions = []; }
                } else if (Array.isArray(betterUser.permissions)) {
                    permissions = sanitizeUserPermissions(betterUser.permissions);
                }
            }

            if (roles.length === 0) roles = ['user'];
            if (permissions.length === 0) permissions = getPermissionsForRoles(roles);

            return {
                id: betterUser.id,
                email: betterUser.email,
                fullName: betterUser.fullName || `${betterUser.firstName ?? ''} ${betterUser.lastName ?? ''}`.trim(),
                firstName: betterUser.firstName ?? '',
                lastName: betterUser.lastName ?? '',
                image: betterUser.image,
                middleName: betterUser.middleName,
                suffix: betterUser.suffix,
                phone: betterUser.phone,
                customFields: betterUser.customFields,
                isActive: betterUser.isActive ?? true,
                cartId: betterUser.cartId,
                defaultConversationId: betterUser.defaultConversationId,
                username: betterUser.username,
                displayUsername: betterUser.displayUsername,
                roles,
                permissions,
                createdAt: betterUser.createdAt ? new Date(betterUser.createdAt) : undefined,
                updatedAt: betterUser.updatedAt ? new Date(betterUser.updatedAt) : undefined,
            };
        };

        const rbacUser = buildRBACUser();

        // Precompute common redirect search params
        const permissionRequiredStr =
            protection.requiredPermissions?.map((p: any) =>
                typeof p === 'string' ? p : `${p.resource}:${p.action}`,
            ).join(',') || undefined;
        const roleRequiredStr = protection.requiredRoles?.join(',') || undefined;
        const redirectStr =
            typeof window !== 'undefined'
                ? window.location.pathname + window.location.search
                : undefined;
        // Current user context to help explain denials
        const currentPermissionsStr = rbacUser?.permissions?.map((p) => `${p.resource}:${p.action}`).join(',') || undefined;
        const currentRolesStr = rbacUser?.roles?.join(',') || undefined;

        // If auth is required but no session/user, redirect to login
        if (protection.requireAuth && !rbacUser) {
            const to = protection.fallbackPath || ROUTE_PATHS.LOGIN;
            throw redirect({
                to,
                search: {
                    redirect: redirectStr,
                    permissionRequired: permissionRequiredStr,
                    roleRequired: roleRequiredStr,
                    currentPermissions: currentPermissionsStr,
                    currentRoles: currentRolesStr,
                },
            });
        }

        // Evaluate access with RBAC utilities
        const allowed = canAccessRoute(rbacUser, protection);
        if (!allowed) {
            const to = protection.fallbackPath || ROUTE_PATHS.ACCESS_DENIED;
            throw redirect({
                to,
                search: {
                    redirect: redirectStr,
                    permissionRequired: permissionRequiredStr,
                    roleRequired: roleRequiredStr,
                    currentPermissions: currentPermissionsStr,
                    currentRoles: currentRolesStr,
                },
            });
        }
        // Otherwise allow navigation
        return;
    };
}

/**
 * Higher-order function to create protected route configurations
 */
export function withRouteProtection(protection: RouteProtection) {
    return {
        beforeLoad: createRouteGuard(protection),
    };
}

/**
 * Utility function to create common protection patterns
 */
export const createProtection = {
    /**
     * Require authentication only
     */
    authenticated: (fallbackPath?: string): RouteProtection => ({
        requireAuth: true,
        fallbackPath: fallbackPath || ROUTE_PATHS.LOGIN,
    }),

    /**
     * Require specific roles
     */
    roles: (roles: string[], requireAll = false, fallbackPath?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: roles as any,
        requireAllRoles: requireAll,
        fallbackPath: fallbackPath || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Require specific permissions
     */
    permissions: (permissions: Array<{ resource: string; action: string }>, requireAll = false, fallbackPath?: string): RouteProtection => ({
        requireAuth: true,
        requiredPermissions: permissions as any,
        requireAllPermissions: requireAll,
        fallbackPath: fallbackPath || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Admin only access
     */
    adminOnly: (fallbackPath?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: ['admin'],
        fallbackPath: fallbackPath || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Editor or higher access
     */
    editorOrHigher: (fallbackPath?: string): RouteProtection => ({
        requireAuth: true,
        requiredRoles: ['editor', 'admin'],
        requireAllRoles: false,
        fallbackPath: fallbackPath || ROUTE_PATHS.ACCESS_DENIED,
    }),

    /**
     * Public access (no restrictions)
     */
    public: (): RouteProtection => ({}),
};

/**
 * Route protection middleware for manual use
 */
export class RouteProtectionMiddleware {
    private protection: RouteProtection;

    constructor(protection: RouteProtection) {
        this.protection = protection;
    }

    async beforeLoad(context: ExtendedRouteContext): Promise<void> {
        const guard = createRouteGuard(this.protection);
        await guard({ context });
    }

    canAccess(auth: AuthContextType): boolean {
        const result = checkRouteAccess(auth, this.protection);
        return result.canAccess;
    }

    getRedirectPath(auth: AuthContextType): string | null {
        const result = checkRouteAccess(auth, this.protection);
        return result.shouldRedirect ? result.redirectPath || null : null;
    }
}

/**
 * Create a route protection middleware instance
 */
export function createRouteProtectionMiddleware(protection: RouteProtection): RouteProtectionMiddleware {
    return new RouteProtectionMiddleware(protection);
}